import axios from "../helpers/bearerAxios";
import { analyzeBoxFileName, isValidBoxUploadFile } from "../helpers/boxUpload";
import { isBoxUploadEnabledForOpportunity } from "../helpers/featureFlags";
import { logger } from "./loggingService";

export type BoxUploadContext = {
    designNumber?: string | null;
    versionNumber?: string | null;
};

export const getDocumentsList = async (docType: string) => {
    return await axios.get(`${import.meta.env.VITE_BASE_URL}${import.meta.env.VITE_FETCH_DOCS_URL}?documentTypeName=${docType}`)
        .then(response => {
            return response.data
        })
        .catch(error => {
            logger.error(error);
            return error;
        })
}


export const downloadEngineeringDocFile = async (payload: any, onProgress: any) => {
    try {
        return await axios.post(`${import.meta.env.VITE_BASE_URL}${import.meta.env.VITE_DWN_ENGG_FILE_URL}`, payload, {
            responseType: 'blob',
            onDownloadProgress: ({ total, loaded }: any) => {
                const percentCompleted = Math.round((loaded * 100) / total);
                onProgress?.(payload.fileName, percentCompleted);
            }
        })
        .then((response) => {
            return response;
        })
        .catch((error) => {
            logger.error(error);
            return error;
        });

    } catch (error) {
        logger.error(error, 'Error downloading file:');
    }
};

const validateBoxUploadFile = (oppId: string, file?: { data?: Blob | null; name?: string | null }, context: BoxUploadContext = {}) => {
    if (!isBoxUploadEnabledForOpportunity(oppId)) {
        logger.error('Box Upload is not enabled');
        return false;
    }

    if (!oppId?.trim()) {
        logger.error('Box Upload skipped: missing opportunity id', { fileName: file?.name });
        return false;
    }

    if (!file?.data) {
        logger.error('Box Upload skipped: missing file data', { fileName: file?.name });
        return false;
    }

    if (!isValidBoxUploadFile(file)) {
        const invalidFile = file as { data?: Blob | null; name?: string | null };
        logger.error('Box Upload skipped: invalid file payload', {
            oppId,
            designNumber: context.designNumber ?? null,
            versionNumber: context.versionNumber ?? null,
            fileName: invalidFile?.name ?? null,
            blobSize: invalidFile?.data?.size ?? 0,
            dataType: invalidFile?.data == null ? 'null' : typeof invalidFile.data,
        });
        return false;
    }
    return true;
}


export const uploadToBox = async (
    oppId: string,
    file: { data?: Blob | null; name?: string | null },
    onProgress: (progress: number) => void,
    context: BoxUploadContext = {},
) => {

    if (!validateBoxUploadFile(oppId, file, context)) {
        return false;
    }

    const fileNameAnalysis = analyzeBoxFileName(file.name ?? '');
    if (fileNameAnalysis.warnings.length > 0) {
        logger.warn('Box upload filename may be rejected by API', {
            oppId,
            designNumber: context.designNumber ?? null,
            versionNumber: context.versionNumber ?? null,
            fileName: file.name,
            fileNameAnalysis,
        });
    }

    const url = `${import.meta.env.VITE_BASE_URL}/api/Opportunities/${oppId}/BoxFiles`;
    const payloadSummary = {
        oppId,
        designNumber: context.designNumber ?? null,
        versionNumber: context.versionNumber ?? null,
        fileName: file.name,
        fileNameAnalysis,
        blobSize: file.data?.size ?? 0,
        blobType: file.data?.type || '(empty)',
        formField: 'file',
        url,
    };

    logger.info('Uploading file to Box', payloadSummary);

    const formData = new FormData();
    formData.append('file', file.data!, file.name!);
    const startedAt = performance.now();

    return await axios.post(url, formData, {
            onUploadProgress : (progressEvent: any) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                  onProgress(percentCompleted);
            }
        })
        .then((response) => {
            logger.info('Box upload succeeded', {
                ...payloadSummary,
                status: response?.status,
                durationMs: Math.round(performance.now() - startedAt),
            });
            return true;
        })
        .catch((error) => {
            const status = error?.response?.status;
            const responseData = serializeAxiosResponseData(error?.response?.data);
            const failureContext = {
                event: status === 400 ? 'BoxUploadBadRequest' : 'BoxUploadFailed',
                status: status ?? null,
                statusText: error?.response?.statusText ?? null,
                responseData,
                payload: payloadSummary,
                durationMs: Math.round(performance.now() - startedAt),
                axiosMessage: error?.message ?? null,
                errorCode: error?.code ?? null,
            };

            if (status === 400) {
                logger.error(
                    failureContext,
                    `Box upload returned 400 for '${file.name}' — request payload and API response logged for debugging`,
                );
            } else {
                logger.error(
                    failureContext,
                    `Box upload failed for '${file.name}' (status: ${status ?? 'unknown'})`,
                );
            }
            return false;
        });
};

export const uploadToBoxDirectly = async (
    oppId: string,
    file: { data?: Blob | null; name?: string | null },
    onProgress: (progress: number) => void,
    context: BoxUploadContext = {},
) => {
    if (!validateBoxUploadFile(oppId, file, context)) {
        return false;
    }

    const fileNameAnalysis = analyzeBoxFileName(file.name ?? '');
    if (fileNameAnalysis.warnings.length > 0) {
        logger.warn('Box upload filename may be rejected by API', {
            oppId,
            designNumber: context.designNumber ?? null,
            versionNumber: context.versionNumber ?? null,
            fileName: file.name,
            fileNameAnalysis,
        });
    }

    const url = `${import.meta.env.VITE_BASE_URL}/api/Opportunities/${oppId}/DirectUploadBoxFile`;
    const payloadSummary = {
        oppId,
        designNumber: context.designNumber ?? null,
        versionNumber: context.versionNumber ?? null,
        fileName: file.name,
        fileNameAnalysis,
        blobSize: file.data?.size ?? 0,
        blobType: file.data?.type || '(empty)',
        formField: 'file',
        url,
    };

    logger.info('Uploading file to Box', payloadSummary);

    const formData = new FormData();
    formData.append('file', file.data!, file.name!);
    const startedAt = performance.now();

    return await axios.post(url, formData, {
            onUploadProgress : (progressEvent: any) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                  onProgress(percentCompleted);
            }
        })
        .then((response) => {
            console.log('response', response);
            if(response?.data?.Success) {
                logger.info('Box upload succeeded', {
                    ...payloadSummary,
                    status: response?.status,
                    durationMs: Math.round(performance.now() - startedAt),
                });
                return true;
            } else {
                logger.error('Box upload failed', {
                    ...payloadSummary,
                    status: response?.status,
                    durationMs: Math.round(performance.now() - startedAt),
                });
                return false;
            }
        })
        .catch((error) => {
            const status = error?.response?.status;
            const responseData = serializeAxiosResponseData(error?.response?.data);
            const failureContext = {
                event: status === 400 ? 'BoxUploadBadRequest' : 'BoxUploadFailed',
                status: status ?? null,
                statusText: error?.response?.statusText ?? null,
                responseData,
                payload: payloadSummary,
                durationMs: Math.round(performance.now() - startedAt),
                axiosMessage: error?.message ?? null,
                errorCode: error?.code ?? null,
            };

            if (status === 400) {
                logger.error(
                    failureContext,
                    `Box upload returned 400 for '${file.name}' — request payload and API response logged for debugging`,
                );
            } else {
                logger.error(
                    failureContext,
                    `Box upload failed for '${file.name}' (status: ${status ?? 'unknown'})`,
                );
            }
            return false;
        });
}

export const generateDirectLayoutInBox = async (sfid: string, customerAddress: string) => {
    if (!sfid?.trim()) {
        logger.error('Direct Layout generation skipped: missing opportunity id', { customerAddress });
        return false;
    }

    if (!customerAddress?.trim()) {
        logger.error('Direct Layout generation skipped: missing customer address', { sfid });
        return false;
    }

    const url = `${import.meta.env.VITE_BASE_URL}/api/Opportunities/DirectLayout/${encodeURIComponent(sfid)}?customerAddress=${encodeURIComponent(customerAddress)}`;

    try {
        await axios.get(url);
        logger.info('Direct Layout generation in Box succeeded', { url });
        return true;
    } catch (error) {
        logger.error(error, 'Direct Layout generation in Box failed');
        return false;
    }
};

export type BoxFileStatus = {
    Id: string;
    Name: string;
    Etag: string;
    ModifiedUtc: string;
};

export const getBoxUploadStatus = async (oppId: string): Promise<BoxFileStatus[]> => {
    try {
        const url = `${import.meta.env.VITE_BASE_URL}/api/Opportunities/GetBoxFiles/${oppId}`;
        const response = await axios.get(url);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        logger.error(error, 'Failed to fetch Box upload status');
        throw error;
    }
};

/** Keep App Insights payloads JSON-serializable (avoid Blobs / circular refs). */
function serializeAxiosResponseData(data: unknown): unknown {
    if (data == null) return null;
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch {
            return data.slice(0, 2000);
        }
    }
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        return { type: 'Blob', size: data.size, blobType: data.type || '(empty)' };
    }
    try {
        return JSON.parse(JSON.stringify(data));
    } catch {
        return String(data).slice(0, 2000);
    }
}

export const downloadPackingSlipDocument = async (
    customerNumber: string,
    shorten: boolean,
): Promise<Blob> => {
    try {
        const response = await axios.post(
            `${import.meta.env.VITE_ONEDOC_BASE_URL}/api/OneDoc/PackingSlip`,
            {
                CustomerNumber: customerNumber,
                Shorten: shorten,
            },
            {
                responseType: 'blob',
            });

        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
}

export const downloadDirectLayout = async (opportunitySfId: string): Promise<Blob> => {
    try {
        const response = await axios.get(
            `${import.meta.env.VITE_ONEDOC_BASE_URL}/api/Documents/DirectLayout/${opportunitySfId}`,
            {
                responseType: 'blob',
            });

        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
}